/*
 * Contact my CSR form model. Field limits and rules carry over from MatNav
 * contact_csr/contactCSR.jsp so CSR inboxes get the same data.
 */

export const CONTACT_CSR_PATH = '/d/contact-csr';

export const CONTACT_CSR_MAX_LENGTH = {
  firstName: 25,
  lastName: 15,
  phone: 16,
  email: 50,
  bookingNumber: 30,
  containerNumber: 30,
  message: 2000,
};

export type ContactCsrField = keyof typeof CONTACT_CSR_MAX_LENGTH;

export type ContactCsrForm = Record<ContactCsrField, string>;

export const CONTACT_CSR_EMPTY_FORM: ContactCsrForm = {
  firstName: '',
  lastName: '',
  phone: '',
  email: '',
  bookingNumber: '',
  containerNumber: '',
  message: '',
};

/** Field order for focusing the first invalid field. */
export const CONTACT_CSR_FIELD_ORDER: ContactCsrField[] = [
  'firstName',
  'lastName',
  'phone',
  'email',
  'bookingNumber',
  'containerNumber',
  'message',
];

/** MatNav accepted digits, spaces, parentheses, dashes and # (extensions). */
const PHONE_PATTERN = /^[0-9() \-#]+$/;

type ContactPhone = {
  areaCode?: string;
  exchangeCode?: string;
  stationCode?: string;
  extension?: string;
};

type ContactProfile = {
  webId?: string;
  contact?: ContactPhone & {
    firstName?: string;
    lastName?: string;
  };
};

/**
 * Profile phone parts → "808-555-0142 #12". Compact so number + extension
 * fit MatNav's 16-character phone limit; the extension is dropped if not.
 */
function buildPhone(contact?: ContactPhone): string {
  const parts = [contact?.areaCode, contact?.exchangeCode, contact?.stationCode]
    .map((part) => `${part || ''}`.trim())
    .filter(Boolean);
  const number = parts.join('-');
  const ext = `${contact?.extension || ''}`.trim();
  const withExt = ext && number ? `${number} #${ext}` : number;
  return withExt.length <= CONTACT_CSR_MAX_LENGTH.phone ? withExt : number;
}

/**
 * Prefill from the profile lookup (URL_UserProfileLookupUpdate response:
 * { webId, contact: { firstName, lastName, areaCode, exchangeCode,
 * stationCode, extension, ... } } — the API names the sign-in email `webId`) plus optional
 * page context (booking / container the user was looking at).
 */
export function buildContactCsrInitialForm(
  profile?: ContactProfile | null,
  context: { bookingNumber?: string; containerNumber?: string } = {},
): ContactCsrForm {
  const contact = profile?.contact || {};
  return {
    ...CONTACT_CSR_EMPTY_FORM,
    firstName: `${contact.firstName || ''}`.slice(0, CONTACT_CSR_MAX_LENGTH.firstName),
    lastName: `${contact.lastName || ''}`.slice(0, CONTACT_CSR_MAX_LENGTH.lastName),
    phone: buildPhone(contact).slice(0, CONTACT_CSR_MAX_LENGTH.phone),
    email: `${profile?.webId || ''}`.toLowerCase().slice(0, CONTACT_CSR_MAX_LENGTH.email),
    bookingNumber: `${context.bookingNumber || ''}`.toUpperCase(),
    containerNumber: `${context.containerNumber || ''}`.toUpperCase(),
  };
}

export type ContactCsrErrors = Partial<Record<ContactCsrField, string>>;

/** Field → i18n key of the error; empty when valid. */
export function validateContactCsrForm(form: ContactCsrForm): ContactCsrErrors {
  const errors: ContactCsrErrors = {};
  if (!form.firstName.trim()) errors.firstName = 'contactCsr.errFirstName';
  if (!form.lastName.trim()) errors.lastName = 'contactCsr.errLastName';
  if (!form.phone.trim()) errors.phone = 'contactCsr.errPhoneRequired';
  else if (!PHONE_PATTERN.test(form.phone.trim())) errors.phone = 'contactCsr.errPhoneFormat';
  const email = form.email.trim();
  const at = email.indexOf('@');
  if (at < 1 || email.indexOf('.', at) === -1) errors.email = 'contactCsr.errEmail';
  if (!form.message.trim()) errors.message = 'contactCsr.errMessage';
  return errors;
}

export function firstInvalidField(errors: ContactCsrErrors): ContactCsrField | null {
  return CONTACT_CSR_FIELD_ORDER.find((field) => errors[field]) || null;
}

export type ContactCsrPayload = ContactCsrForm;

/**
 * Request body for the Contact my CSR endpoint. The server adds the parts
 * the browser must not control: the assigned CSR address (from the signed-in
 * email), the subject "Web Message from <email>", and org/city/state.
 */
export function buildContactCsrPayload(form: ContactCsrForm): ContactCsrPayload {
  return {
    firstName: form.firstName.trim(),
    lastName: form.lastName.trim(),
    phone: form.phone.trim(),
    email: form.email.trim().toLowerCase(),
    bookingNumber: form.bookingNumber.trim().toUpperCase(),
    containerNumber: form.containerNumber.trim().toUpperCase(),
    message: form.message.trim(),
  };
}
